import React from "react";
import wordmark from "../../../assets/brand/cache-wordmark.svg";
import backChevron from "../../../assets/brand/onboarding/back-chevron.svg";
import { useFitScale, fitScaleStyle } from "../../ForBrands/useFitScale";
import { PhoneThemeProvider, useIsPhone } from "../../ForBrands/useIsPhone";
import { MobileStackHeader } from "../../ForBrands/ui/mobile/MobileShell";
import "../../ForBrands/brandMode.css";
import "./onboarding.css";

// Figma "OnboardingShell · Web": a topbar with 48px side padding and 22px
// vertical padding (a 40px back square, 18px gap, the 95x19 wordmark; the
// step title or the Step N of 4 progress in the middle; one quiet action on
// the right), a 1px rule, then the stage: 72px above, 96px below, a 560px
// column (880px for the wide result screens) with 28px between blocks.
//
// Below 640px the frame follows the mobile "MobileShell" (1767:735): the
// stack header (back chevron or close X, the title or "Step N of M" with its
// bars, the action as orange text), no wordmark and no rule, then a 24px
// gutter column with 18px between blocks whose `Actions` footer sticks to
// the bottom (onboarding.css). Both layouts share one element tree below the
// header, so a page keeps its state when the window crosses 640px.

export const StepProgress = ({ step, total }) => (
  <div className="flex flex-col items-center gap-2">
    <span className="bm-label-l text-bm-muted">Step {step} of {total}</span>
    <div className="flex items-start gap-[6px]">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className={`h-1 w-[72px] rounded-[4px] ${i < step ? "bg-bm-orange" : "bg-bm-mutedbg"}`} />
      ))}
    </div>
  </div>
);

const DesktopHeader = ({ title, progress, onBack, action }) => (
  <header className="sticky top-0 z-20 bg-white">
    <div className="flex items-center justify-between gap-4 px-5 py-[22px] sm:px-12">
      <div className="flex items-center gap-[18px]">
        {onBack ? (
          <button type="button" onClick={onBack} aria-label="Back" className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-bm-subtle hover:bg-bm-mutedbg">
            <img src={backChevron} alt="" className="h-5 w-5" />
          </button>
        ) : (
          <span className="h-10 w-10" aria-hidden="true" />
        )}
        <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
      </div>
      <div className="min-w-0 text-center">
        {progress ? <StepProgress {...progress} /> : title ? <span className="bm-label-l text-bm-muted">{title}</span> : <span className="bm-label-l">&#8203;</span>}
      </div>
      <div className="flex shrink-0 justify-end">
        {/* The right-hand action: quiet text by default, or an outlined
            pill (`action.pill`) when it leads somewhere else. */}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className={action.pill
              ? "inline-flex h-9 items-center rounded-full border border-bm-border bg-white px-4 bm-label-l text-bm-ink hover:bg-bm-subtle"
              : "bm-label-l text-bm-muted hover:text-bm-ink"}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
    <div className="h-px w-full bg-bm-border" />
  </header>
);

// Phone main top padding: 8px under the Welcome / Log in lockup, 16px under
// the step bars (Figma 04 1759:1290, 2466:18947), none when a hero band sits
// between the header and the body, 24px otherwise.
const PHONE_TOP = { lockup: "pt-2", steps: "pt-4", hero: "pt-0" };

// The phone root fills the screen: 100dvh, or 100vh in browsers without
// dynamic viewport units (iOS Safari before 15.4), through an @supports not
// rule, so the result does not hang on the order Tailwind emits the two in.
//
// The header row (MobileStackHeader's three-column grid) is 4px between
// columns here instead of 8px. At 320px that keeps "Step N of 4" centred
// beside the widest right action, "Save and log out" (104px): with 8px the
// right column's share fell 2px short of the action, so the grid moved the
// label 2px left. Every title and action pair the frame shows fits centred
// at 320 this way; a longer pair still truncates the centre (D-o1).
const PHONE_ROOT =
  "bm-root flex min-h-dvh w-full min-w-0 flex-col bg-white supports-[not(height:100dvh)]:min-h-screen [&>header>div:first-child]:gap-x-1";

// The phone header's right slot is always orange text (never a pill): the
// short `phoneLabel` when there is one, named by `ariaLabel`.
const phoneActionOf = (action) =>
  action ? { label: action.phoneLabel || action.label, onClick: action.onClick, ariaLabel: action.ariaLabel } : undefined;

// Props (all optional):
// - title, progress ({ step, total }), onBack, action ({ label, onClick,
//   pill }), wide, band: as always, at every width.
// - onClose: phones only. The header's left button becomes the close X
//   (ahead of onBack); the desktop header keeps its back slot.
// - action.phoneLabel / action.ariaLabel: phones only. The visible label and
//   the accessible name of the right-slot text ("Browse", "Browse the Player
//   Index"); the desktop button keeps `label` as its text and name.
// - phoneLogo: phones only. Centres the 60px wordmark when there is no title
//   or progress (404, no access, the admin user view).
// - phoneTop: phones only. "lockup" (8px, Welcome and Log in), "steps"
//   (16px under the step bars, the Name step) or "hero" (0); the default is
//   24px, and 0 whenever `band` is set.
const AuthFrame = ({ title, progress, onBack, onClose, action, wide = false, band, phoneLogo = false, phoneTop, children }) => {
  const fit = useFitScale();
  const phone = useIsPhone();
  const phoneTopClass = band ? "pt-0" : PHONE_TOP[phoneTop] || "pt-6";
  return (
    <PhoneThemeProvider>
      <div
        className={phone ? PHONE_ROOT : "bm-root min-h-screen w-full min-w-0 bg-white"}
        style={fitScaleStyle(fit)}
      >
        {phone ? (
          <MobileStackHeader
            title={title}
            progress={progress}
            leading={onClose ? "close" : onBack ? "back" : null}
            onLeading={onClose || onBack}
            action={phoneActionOf(action)}
            logo={phoneLogo}
          />
        ) : (
          <DesktopHeader title={title} progress={progress} onBack={onBack} action={action} />
        )}
        {band}
        <main className={phone ? `flex w-full flex-1 flex-col px-6 ${phoneTopClass}` : "flex w-full flex-col items-center px-5 pb-[96px] pt-[72px]"}>
          <div
            className={phone
              ? `ob-frame flex w-full flex-1 flex-col gap-[18px]${wide ? " ob-frame--wide" : ""}`
              : `flex w-full flex-col gap-[28px] ${wide ? "max-w-[880px]" : "max-w-[560px]"}`}
          >
            {children}
          </div>
        </main>
      </div>
    </PhoneThemeProvider>
  );
};

export default AuthFrame;
